import { useIntl } from 'react-intl';

import { ZoomToSelectionIcon } from '@dynatrace/strato-icons';

import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { getCustomContextMenuItems } from '../../../../core/components/interactions-overlay/utils/get-custom-context-menu-items.js';
import { OverlayTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import { SelectionTooltipWithAsyncActions } from '../../../../core/components/selection-area/SelectionTooltipWithAsyncActions.js';
import { getSelectionTooltipPosition } from '../../../../core/components/selection-area/tooltip/get-selection-tooltip-position.js';
import { usePointerAndScrollResponsiveChartBounds } from '../../../../core/hooks/usePointerAndScrollResponsiveChartBounds.js';
import { useSelectState } from '../../../hooks/store/selectors.js';
import { useHistogramChartOverlayRef } from '../../../hooks/useHistogramChartOverlayRef.js';
import { useHistogramFilteredData } from '../../../hooks/useHistogramFilteredData.js';
import { useHistogramInteractions } from '../../../hooks/useHistogramInteractions.js';
import { useHistogramSelectConfig } from '../../../hooks/useHistogramSelectConfig.js';
import { useHistogramXFormatter } from '../../../hooks/useHistogramXFormatter.js';
import { useHistogramXScale } from '../../../hooks/useHistogramXScale.js';
import { useZoomToSelection } from '../../../hooks/useZoomToSelection.js';
import { HISTOGRAM_MESSAGES } from '../../../messages/tooltip-text.js';
import {
  type HistogramBin,
  HistogramSeries,
} from '../../../types/histogram.js';
import { isBinWithinRange } from '../utils/is-bin-within-range.js';

export const HistogramSelectionAreaTooltip = () => {
  const selectState = useSelectState();
  const zoomToSelection = useZoomToSelection();
  /* eslint-disable-next-line @typescript-eslint/no-deprecated */
  const { zoomX } = useHistogramInteractions();
  const filteredData = useHistogramFilteredData();
  const { xScale } = useHistogramXScale();
  const formatter = useHistogramXFormatter();
  const selectConfig = useHistogramSelectConfig();
  const { width } = useGridPlotSize();
  const intl = useIntl();
  const chartRef = useHistogramChartOverlayRef();
  const chartBounds = usePointerAndScrollResponsiveChartBounds(
    chartRef,
    true,
  )();

  if (!selectState || !chartBounds) {
    return null;
  }

  const selectionStart = selectState.selectionPercentage
    ? (width * selectState.selectionPercentage[0]) / 100
    : selectState.start.relative.x;
  const selectionEnd = selectState.selectionPercentage
    ? (width * selectState.selectionPercentage[1]) / 100
    : selectState.end.relative.x;

  const range: [number, number] = [
    xScale.invert(selectionStart),
    xScale.invert(selectionEnd),
  ];

  const selectedSeries: HistogramSeries[] = [];
  let totalBins = 0;
  const [from, to] = range;
  for (const series of filteredData.series) {
    let isSeriesPresent = false;
    const seriesBins: HistogramBin[] = [];

    for (const bin of series.bins) {
      if (isBinWithinRange(bin, from, to)) {
        totalBins++;
        seriesBins.push(bin);
        isSeriesPresent = true;
      }
    }

    if (isSeriesPresent) {
      selectedSeries.push({ ...series, bins: seriesBins });
    }
  }

  const tooltipPosition = getSelectionTooltipPosition(selectState, chartBounds);

  return (
    <SelectionTooltipWithAsyncActions
      actions={() => {
        const actions = selectConfig?.actions;
        return (
          actions &&
          (async () => actions(selectedSeries, range))().then(
            getCustomContextMenuItems,
          )
        );
      }}
      selectFinished={selectState.finished}
      header={
        <OverlayTooltip.Header
          header1={[formatter(from), formatter(to)]}
          duration1={formatter(Math.abs(to - from))}
          subheader={`${totalBins} bins across ${selectedSeries.length} series`}
        />
      }
      position={tooltipPosition}
      zoomEnabled={!!zoomX?.enabled}
      range={range}
    >
      {zoomX?.enabled ? (
        <OverlayTooltip.Action
          label={intl.formatMessage(HISTOGRAM_MESSAGES.zoomToSelection)}
          icon={<ZoomToSelectionIcon />}
          onSelect={zoomToSelection}
          trailingIcon="⏎"
        />
      ) : null}
    </SelectionTooltipWithAsyncActions>
  );
};
